// components/SupplierFinancials.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Container,
  Pagination,
  Stack
} from "@mui/material";
import {
  Payment,
  Receipt,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Schedule,
  CheckCircle,
  Error as ErrorIcon,
  Visibility,
  Add,
  AccountBalanceWallet,
  ReceiptLong,
  AttachMoney,
  Warning,
  ArrowBack
} from "@mui/icons-material";
import { suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
  </div>
);

export const SupplierFinancials = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [financialData, setFinancialData] = useState(null);
  const [grns, setGrns] = useState([]);
  const [unpaidGrns, setUnpaidGrns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [quickPayment, setQuickPayment] = useState({
    amount: "",
    paymentMethod: "bank_transfer",
    paymentDate: new Date().toISOString().split("T")[0],
    reference: "",
    notes: "",
  });

  // Pagination states
  const [grnPagination, setGrnPagination] = useState({
    current: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const [unpaidGrnPagination, setUnpaidGrnPagination] = useState({
    current: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const [paymentPagination, setPaymentPagination] = useState({
    current: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    fetchFinancialData();
    fetchGRNs();
    fetchUnpaidGRNs();
    fetchPayments();
  }, [id]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchGRNs();
    } else if (tabValue === 1) {
      fetchUnpaidGRNs();
    } else if (tabValue === 2) {
      fetchPayments();
    }
  }, [tabValue, grnPagination.page, unpaidGrnPagination.page, paymentPagination.page]);

  const fetchFinancialData = async () => {
    try {
      const response = await suppliersAPI.getFinancialSummary(id);
      setFinancialData(response.data.data);
    } catch (error) {
      enqueueSnackbar("Error fetching financial data", { variant: "error" });
    }
  };

  const fetchGRNs = async () => {
    try {
      const response = await suppliersAPI.getGRNs(id, {
        page: grnPagination.page,
        limit: grnPagination.limit,
      });
      const { grns: grnsData, pagination: paginationData } = response.data.data;
      setGrns(grnsData);
      setGrnPagination(prev => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching GRNs", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchUnpaidGRNs = async () => {
    try {
      const response = await suppliersAPI.getUnpaidGRNs(id, {
        page: unpaidGrnPagination.current,
        limit: unpaidGrnPagination.limit,
      });

      const { unpaidGRNs: unpaidGrnsData, summary, pagination } = response.data.data;

      setUnpaidGrns(unpaidGrnsData);

      setUnpaidGrnPagination({
        current: pagination?.current || 1,
        limit: pagination?.limit || unpaidGrnPagination.limit,
        total: pagination?.total || summary?.totalUnpaid || unpaidGrnsData.length,
        pages: pagination?.pages ||
          Math.ceil((summary?.totalUnpaid || unpaidGrnsData.length) / unpaidGrnPagination.limit),
      });

    } catch (error) {
      console.error("Error fetching unpaid GRNs:", error);
    }
  };


  const fetchPayments = async () => {
    try {
      const response = await suppliersAPI.getPayments(id, {
        page: paymentPagination.page,
        limit: paymentPagination.limit,
      });
      const { payments: paymentsData, pagination: paginationData } = response.data.data;
      setPayments(paymentsData);
      setPaymentPagination(prev => ({
        ...prev,
        current: paginationData?.current,   // 🔥 THIS WAS MISSING
        limit: paginationData?.limit,
        total: paginationData?.total,
        pages: paginationData?.pages,
      }));
    } catch (error) {
      console.error("Error fetching payments:", error);
    }
  };

  const handleGrnPageChange = (event, value) => {
    setGrnPagination(prev => ({ ...prev, page: value }));
  };

  const handleUnpaidGrnPageChange = (event, value) => {
    setUnpaidGrnPagination(prev => ({ ...prev, page: value }));
  };

  const handlePaymentPageChange = (event, value) => {
    setPaymentPagination(prev => ({ ...prev, page: value }));
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: "success",
      draft: "default",
      received: "info",
      verified: "warning",
      pending: "warning",
      paid: "success",
      partially_paid: "info",
    };
    return colors[status] || "default";
  };

  const handleViewGRN = (grn) => {
    navigate(`/hospital/pharmacy/grns/${grn._id}`);
  };

  const handlePayGRN = (grn) => {
    setSelectedGRN(grn);
    setQuickPayment({
      amount: grn.totalAmount - (grn.paidAmount || 0),
      paymentMethod: "bank_transfer",
      paymentDate: new Date().toISOString().split("T")[0],
      reference: "",
      notes: `Payment for GRN ${grn.grnNumber}`,
    });
    setPaymentDialog(true);
  };

  const handleQuickPayment = async () => {
    try {
      const paymentData = {
        ...quickPayment,
        amount: parseFloat(quickPayment.amount),
        paymentType: "grn_payment",
        status: "completed",
        grnId: selectedGRN._id,
      };

      await suppliersAPI.addPayment(id, paymentData);

      enqueueSnackbar("Payment recorded successfully", { variant: "success" });
      setPaymentDialog(false);
      setSelectedGRN(null);

      // Refresh data
      fetchFinancialData();
      fetchGRNs();
      fetchUnpaidGRNs();
      fetchPayments();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error recording payment",
        { variant: "error" }
      );
    }
  };

  const handleNavigateToPayments = () => {
    navigate(`/hospital/pharmacy/suppliers/${id}/payments`);
  };

  const handleNavigateToReturns = () => {
    navigate(`/hospital/pharmacy/suppliers/${id}/returns`);
  };

  // Calculate additional metrics
  const calculateMetrics = () => {
    if (!financialData) return {};

    const totalGRNValue = financialData.financialSummary?.totalGRNValue || 0;
    const totalPayments = financialData.financialSummary?.totalPayments || 0;
    const outstandingBalance = financialData.financialSummary?.outstandingBalance || 0;

    return {
      paymentCoverage: totalGRNValue > 0 ? (totalPayments / totalGRNValue) * 100 : 0,
      averageGRNValue: financialData.financialSummary?.grnCount > 0
        ? totalGRNValue / financialData.financialSummary.grnCount
        : 0,
    };
  };

  const metrics = calculateMetrics();

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)" }}>
        <Container maxWidth="xl" sx={{ py: 5 }}>
          <LinearProgress />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)" }}>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header with Actions */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/hospital/pharmacy/suppliers")}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Back to Suppliers
            </Button>
            <Typography variant="h3" fontWeight="bold">
              Financial Overview
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ReceiptLong />}
              onClick={handleNavigateToReturns}
              sx={{ borderRadius: 2 }}
            >
              View Returns
            </Button>
            <Button
              variant="contained"
              startIcon={<Payment />}
              onClick={handleNavigateToPayments}
              sx={{ borderRadius: 2, boxShadow: 2, transition: "0.3s", "&:hover": { transform: "scale(1.03)" } }}
            >
              Manage Payments
            </Button>
          </Box>
        </Box>

        {/* Enhanced Financial Summary Cards */}
        {financialData && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Receipt sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
                  <Typography variant="h3" fontWeight="bold" color="primary.main">
                    ₹{financialData.financialSummary?.totalGRNValue?.toLocaleString()}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Total GRN Value
                  </Typography>
                  <Chip
                    label={`${financialData.financialSummary?.grnCount} GRNs`}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1, borderRadius: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Payment sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    ₹{financialData.financialSummary?.totalPayments?.toLocaleString()}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Total Payments
                  </Typography>
                  <Chip
                    label={`${metrics.paymentCoverage.toFixed(1)}% Coverage`}
                    size="small"
                    color={metrics.paymentCoverage > 80 ? "success" : "warning"}
                    sx={{ mt: 1, borderRadius: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <AccountBalanceWallet sx={{ fontSize: 40, color: "warning.main", mb: 1 }} />
                  <Typography variant="h3" fontWeight="bold" color="warning.main">
                    ₹{financialData.financialSummary?.outstandingBalance?.toLocaleString()}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Outstanding Balance
                  </Typography>
                  {financialData.financialSummary?.outstandingBalance > 0 && (
                    <Chip
                      icon={<Warning />}
                      label="Payment Due"
                      size="small"
                      color="error"
                      sx={{ mt: 1, borderRadius: 2 }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Schedule sx={{ fontSize: 40, color: "error.main", mb: 1 }} />
                  <Typography variant="h3" fontWeight="bold" color="error.main">
                    ₹{financialData.financialSummary?.pendingGRNValue?.toLocaleString()}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Unpaid GRNs
                  </Typography>
                  <Chip
                    label={`${financialData.financialSummary?.unpaidGRNCount} pending`}
                    size="small"
                    color="error"
                    sx={{ mt: 1, borderRadius: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <TrendingUp sx={{ fontSize: 40, color: "info.main", mb: 1 }} />
                  <Typography variant="h3" fontWeight="bold" color="info.main">
                    ₹{financialData.financialSummary?.pendingPOValue?.toLocaleString()}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Pending POs
                  </Typography>
                  <Chip
                    label={`${financialData.financialSummary?.pendingPOCount} orders`}
                    size="small"
                    color="info"
                    sx={{ mt: 1, borderRadius: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Recent Activity Summary */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptLong /> Recent GRNs
                </Typography>
                {financialData?.recentGRNs?.length > 0 ? (
                  <Box>
                    {financialData.recentGRNs.slice(0, 5).map((grn, index) => (
                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
                        <Typography variant="h6">{grn.grnNumber}</Typography>
                        <Typography variant="h6" fontWeight="medium">
                          ₹{grn.totalAmount?.toLocaleString()}
                        </Typography>
                        <Chip
                          label={grn.isPaid ? "Paid" : "Unpaid"}
                          size="small"
                          color={grn.isPaid ? "success" : "warning"}
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>No recent GRNs</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney /> Recent Payments
                </Typography>
                {financialData?.recentPayments?.length > 0 ? (
                  <Box>
                    {financialData.recentPayments.slice(0, 5).map((payment, index) => (
                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
                        <Box>
                          <Typography variant="h6">{payment.paymentType?.replace('_', ' ')}</Typography>
                          <Typography variant="body1" color="text.secondary">
                            {new Date(payment.paymentDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight="medium" color="success.main">
                          ₹{payment.amount?.toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>No recent payments</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs for Detailed Management */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="All GRNs" />
              <Tab label="Unpaid GRNs" />
              <Tab label="Recent Payments" />
              <Tab label="Financial Analytics" />
            </Tabs>
          </CardContent>
        </Card>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <GRNTable
            grns={grns}
            onView={handleViewGRN}
            onPay={handlePayGRN}
            getStatusColor={getStatusColor}
            pagination={grnPagination}
            onPageChange={handleGrnPageChange}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <GRNTable
            grns={unpaidGrns}
            onView={handleViewGRN}
            onPay={handlePayGRN}
            getStatusColor={getStatusColor}
            showPayButton={true}
            pagination={unpaidGrnPagination}
            onPageChange={handleUnpaidGrnPageChange}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <PaymentTable
            payments={payments}
            pagination={paymentPagination}
            onPageChange={handlePaymentPageChange}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <FinancialAnalytics financialData={financialData} />
        </TabPanel>

        {/* Quick Payment Dialog */}
        <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle>
            <Typography variant="h5" fontWeight="bold">
              Quick Payment - {selectedGRN?.grnNumber}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  GRN Amount: ₹{selectedGRN?.totalAmount?.toLocaleString()}
                  {selectedGRN?.paidAmount > 0 && (
                    <span> (Already Paid: ₹{selectedGRN.paidAmount?.toLocaleString()})</span>
                  )}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Payment Amount"
                  value={quickPayment.amount}
                  onChange={(e) => setQuickPayment(prev => ({ ...prev, amount: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Payment Method"
                  value={quickPayment.paymentMethod}
                  onChange={(e) => setQuickPayment(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Payment Date"
                  value={quickPayment.paymentDate}
                  onChange={(e) => setQuickPayment(prev => ({ ...prev, paymentDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reference"
                  value={quickPayment.reference}
                  onChange={(e) => setQuickPayment(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Transaction ID, Cheque No."
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  value={quickPayment.notes}
                  onChange={(e) => setQuickPayment(prev => ({ ...prev, notes: e.target.value }))}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentDialog(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button
              onClick={handleQuickPayment}
              variant="contained"
              disabled={!quickPayment.amount || quickPayment.amount <= 0}
              sx={{ borderRadius: 2 }}
            >
              Record Payment
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

// Enhanced GRN Table Component with Fixed Pagination
const GRNTable = ({ grns, onView, onPay, getStatusColor, showPayButton = false, pagination, onPageChange }) => {
  // Calculate correct showing range
  const startItem = ((pagination?.current || 1) - 1) * (pagination?.limit || 10) + 1;
  const endItem = Math.min(
    (pagination?.current || 1) * (pagination?.limit || 10),
    pagination?.total || grns.length
  );

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Goods Received Notes ({pagination?.total || grns.length})
          </Typography>
          {showPayButton && grns.length > 0 && (
            <Chip
              label={`Total Due: ₹${grns.reduce((sum, grn) => sum + (grn.totalAmount - (grn.paidAmount || 0)), 0).toLocaleString()}`}
              color="error"
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
          )}
        </Box>

        {grns.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>No GRNs found.</Alert>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>GRN Number</TableCell>
                    <TableCell>PO Number</TableCell>
                    <TableCell>Receipt Date</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="right">Paid Amount</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grns.map((grn) => {
                    const paidAmount = grn.paidAmount || 0;
                    const balance = grn.totalAmount - paidAmount;

                    return (
                      <TableRow key={grn._id} hover>
                        <TableCell>
                          <Typography variant="h6" fontWeight="bold">
                            {grn.grnNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6">
                            {grn.purchaseOrder?.poNumber || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6">
                            {new Date(grn.receiptDate).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" fontWeight="bold">
                            ₹{grn.totalAmount?.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" color="success.main">
                            ₹{paidAmount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color={balance > 0 ? "error.main" : "success.main"}
                          >
                            ₹{balance.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={grn.status}
                            size="small"
                            color={getStatusColor(grn.status)}
                            sx={{ borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell>
                          {balance === 0 ? (
                            <Chip
                              icon={<CheckCircle />}
                              label="Paid"
                              size="small"
                              color="success"
                              sx={{ borderRadius: 2 }}
                            />
                          ) : paidAmount > 0 ? (
                            <Chip
                              icon={<Schedule />}
                              label="Partial"
                              size="small"
                              color="warning"
                              sx={{ borderRadius: 2 }}
                            />
                          ) : (
                            <Chip
                              icon={<ErrorIcon />}
                              label="Unpaid"
                              size="small"
                              color="error"
                              sx={{ borderRadius: 2 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="View GRN Details">
                              <IconButton
                                size="small"
                                onClick={() => onView(grn)}
                                color="primary"
                                sx={{ borderRadius: 2 }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            {showPayButton && balance > 0 && (
                              <Tooltip title="Make Payment">
                                <IconButton
                                  size="small"
                                  onClick={() => onPay(grn)}
                                  color="success"
                                  sx={{ borderRadius: 2 }}
                                >
                                  <Payment />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Fixed Pagination for GRN Table */}
            {pagination && pagination.pages > 1 && (
              <Stack spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
                <Pagination
                  count={pagination.pages}
                  page={pagination.current}
                  onChange={onPageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
                <Typography variant="body2" color="text.secondary">
                  Showing {startItem}-{endItem} of {pagination.total} GRNs
                </Typography>
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Payment Table Component with Fixed Pagination
const PaymentTable = ({ payments, pagination, onPageChange }) => {
  const getPaymentTypeColor = (type) => {
    const colors = {
      grn_payment: "primary",
      advance_payment: "info",
      return_settlement: "success",
      credit_note: "success",
      account_settlement: "secondary",
    };
    return colors[type] || "default";
  };

  // Calculate correct showing range
  const startItem = ((pagination?.current || 1) - 1) * (pagination?.limit || 10) + 1;
  const endItem = Math.min(
    (pagination?.current || 1) * (pagination?.limit || 10),
    pagination?.total || payments.length
  );

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Recent Payments ({pagination?.total || payments.length})
        </Typography>

        {payments.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>No payments found.</Alert>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment._id} hover>
                      <TableCell>
                        <Typography variant="h6">
                          {new Date(payment.paymentDate).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.paymentType?.replace(/_/g, " ")}
                          size="small"
                          color={getPaymentTypeColor(payment.paymentType)}
                          sx={{ borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          {payment.reference || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          {payment.paymentMethod?.replace(/_/g, " ")}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" fontWeight="bold">
                          ₹{payment.amount?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.status}
                          size="small"
                          color={payment.status === 'completed' ? 'success' : 'warning'}
                          sx={{ borderRadius: 2 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Fixed Pagination for Payment Table */}
            {pagination && pagination.pages > 1 && (
              <Stack spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
                <Pagination
                  count={pagination.pages}
                  page={pagination.current}
                  onChange={onPageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
                <Typography variant="body2" color="text.secondary">
                  Showing {startItem}-{endItem} of {pagination.total} payments
                </Typography>
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Financial Analytics Component
const FinancialAnalytics = ({ financialData }) => {
  if (!financialData) {
    return <Alert severity="info" sx={{ borderRadius: 2 }}>No financial data available</Alert>;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Payment Type Breakdown
            </Typography>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Analytics charts coming soon.
            </Alert>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Payment Status
            </Typography>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Payment status analytics coming soon.
            </Alert>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SupplierFinancials;